/**
 * Auth identity reconciliation.
 *
 * The access token is the source of truth for *who the API will act as* — the
 * backend authorises every request by the token's `sub` (user id) claim, never
 * by anything the client stores. The persisted `user` profile is only for
 * display. These two MUST describe the same account, or the UI shows one nurse
 * while the API serves another's data.
 *
 * They can drift because the refresh cookie is domain-wide
 * (`Domain=<env>.tricorder.icu`, shared across `alg.`, `care-planner.`, the main app)
 * while `localStorage` is per-origin. Logging in as account B on one subdomain
 * rewrites the shared cookie to B; a sibling subdomain that still has account A
 * in its `localStorage` then refreshes into a *B* token but keeps A's profile.
 * The symptom: a session shared with A never appears, because the client is
 * silently acting as B.
 *
 * The rule enforced here: whenever a freshly obtained token's subject is known
 * and differs from the stored profile, re-fetch the profile for that token so
 * `user` and `accessToken` always belong to the same account.
 */

import { getApiBaseUrl } from "@/api/baseUrl";
import type { User, UserResponse } from "@/api/types";
import { callerCapabilitiesFrom, toUser } from "@/api/types";
import { useAuthStore } from "@/stores/auth";
import { createLogger } from "@/utils/logger";
import { parseRetryAfter } from "@/lib/httpUtils";

const logger = createLogger("AuthIdentity");

// Statuses that mean "the API cannot answer YET", not "this token is not
// authenticated". The gateway answers 503 ACL_UNAVAILABLE (502 on one path)
// when the OpenFGA engine is cold — after scale-to-zero it takes ~60–90s from
// the first request until checks answer in milliseconds again (observed on
// staging, 11 October 2026: container start at +46s, fast at +77s). A 401 is
// the only definitive "not authenticated"; every other status is a server
// defect, and retrying it would only hold the boot hostage.
const RECONCILE_AVAILABILITY_STATUSES = new Set<number>([502, 503]);

// Backoff between /me attempts while the ACL backend warms up. Six attempts
// with these gaps bridge the observed ~90s cold window even though the
// gateway holds each attempt up to ~30s before answering the 503. These gaps
// are deliberately longer than the generic request-retry ceiling in
// api/client.ts (one retry, ~5s): identity reconciliation is the login
// bootstrap — if it gives up, the session is cleared and the nurse is back on
// the login page — so it carries the cold-start bridge for the whole app
// (Joël, 11 October 2026: min instances stay 0, "the cold start is bridged in
// the callers, where the fail-fast design puts it").
const RECONCILE_RETRY_DELAYS_MS = [2_000, 5_000, 10_000, 15_000, 15_000];

/**
 * Decode a JWT's payload segment. The single JWT decoder for the frontend —
 * JWTs are base64URL (RFC 7515), so the `-`/`_` → `+`/`/` normalisation is
 * load-bearing: a bare `atob` throws on any payload containing those
 * characters, which is token-content-dependent and therefore intermittent.
 * Throws on a non-JWT input; callers own that policy (treat as "unknown",
 * "expired", or fail loud — see each call site).
 */
export function decodeJwtPayload<T>(token: string): T {
  const payload = token.split(".")[1];
  return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as T;
}

/**
 * Decode the `sub` (subject = user id) claim from a JWT access token. Returns
 * null when the token is not a decodable JWT or carries no `sub` — callers
 * treat "unknown subject" as "no conflict" so non-JWT/opaque tokens keep the
 * existing trust-the-token behaviour rather than being force-reconciled.
 */
export function jwtSubject(token: string): string | null {
  try {
    const payload = decodeJwtPayload<{ sub?: unknown }>(token);
    return typeof payload.sub === "string" && payload.sub !== ""
      ? payload.sub
      : null;
  } catch {
    return null;
  }
}

/**
 * True only when we can positively decode the token's subject AND it differs
 * from the stored user's id — i.e. token and profile provably belong to
 * different accounts. An undecodable subject or a null user is NOT a conflict
 * (we can't prove drift, so we don't disrupt the session).
 */
export function tokenConflictsWithUser(
  token: string,
  user: User | null,
): boolean {
  if (!user) return false;
  const sub = jwtSubject(token);
  return sub !== null && sub !== user.id;
}

/**
 * Fetch the authoritative profile for `token` from `/me` and store the
 * token+user together (via `setAuth`, which also marks auth ready), so the
 * displayed identity matches the account the API will act as.
 *
 * Uses a direct `fetch` rather than the `api` client so it never recurses into
 * the 401 → refresh interceptor (which is itself a caller of this function).
 * Availability failures (502/503 — a cold ACL engine — and network errors) are
 * retried with backoff long enough to bridge an OpenFGA scale-to-zero cold
 * start (~90s observed), because this call is the login bootstrap: giving up
 * clears the session and lands the user back on the login page for a condition
 * that self-heals. Definitive answers (401, other statuses) return on the
 * first attempt. Returns false when the profile fetch failed through that
 * budget; the caller decides whether to clear the session.
 *
 * `onlyIfTokenStillActive` is for fire-and-forget (unawaited) callers: if the
 * active session token changes while `/me` is in flight — a logout
 * (token → null) or a different account signing in on a shared bedside device
 * (token → another) — the late response must NOT write, or it would resurrect
 * the logged-out session or cross-write one nurse's profile onto another's
 * token. Awaited init callers omit the flag: they are *establishing* the
 * session and must write unconditionally (the new token isn't in the store
 * yet, so a token-match guard would wrongly skip them).
 */
export async function reconcileIdentity(
  token: string,
  opts?: { onlyIfTokenStillActive?: boolean },
): Promise<boolean> {
  // One attempt per loop turn; availability failures (502/503, network) wait
  // out the ACL engine's cold start and try again, auth failures (401) and
  // server defects return false on the first answer. The caller's contract is
  // unchanged — false still means "do not keep this session" — but after a
  // cold start it is only reached once the engine had every chance to warm.
  for (let attempt = 0; ; attempt++) {
    try {
      const res = await fetch(`${getApiBaseUrl()}/me`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) {
        if (
          attempt < RECONCILE_RETRY_DELAYS_MS.length &&
          RECONCILE_AVAILABILITY_STATUSES.has(res.status)
        ) {
          const retryAfterSeconds = parseRetryAfter(res.headers.get("Retry-After"));
          const delayMs =
            retryAfterSeconds !== null
              ? retryAfterSeconds * 1000
              : RECONCILE_RETRY_DELAYS_MS[attempt];
          logger.warn(
            `Identity reconciliation: /me unavailable (${res.status}), retrying in ${delayMs}ms`,
            { attempt: attempt + 1 },
          );
          await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
          continue;
        }
        logger.error("Identity reconciliation: /me failed", {
          status: res.status,
        });
        return false;
      }
      const userData = (await res.json()) as UserResponse;
      if (
        opts?.onlyIfTokenStillActive &&
        useAuthStore.getState().accessToken !== token
      ) {
        logger.debug(
          "Identity reconciliation skipped: session changed since fetch started",
        );
        return false;
      }
      // Freshen the profile from /me. Since the users cut-over /me carries
      // the PROFILE only — the caller-capability half (admin tier, admin
      // endpoints, launchable agents, location memberships) lives at GET
      // /admin, GET /agents and the boot reads (care-app/ADR-0015) — and
      // toUser defaults every capability it doesn't find. So a same-identity
      // reconcile merges PRESENT keys over the stored user (the
      // callerCapabilitiesFrom shape, same discipline as updateUser): an
      // absent key keeps its resolved value instead of being reset, so the
      // late /me that lands after the tier fetch can no longer strip a
      // resolved admin tier or launchable list — and if /me ever carries a
      // capability again, the present key still wins.
      //
      // isPlatformAdmin additionally keeps its OR-with-prev fail-closed
      // guard: it's a live OpenFGA Check that degrades to false on a
      // COLD/unreachable ACL backend, and a transient outage must not flip
      // a real admin's UI off. A DIFFERENT identity gets `fresh` whole —
      // capabilities are per-caller and never cross identities.
      const prev = useAuthStore.getState().user;
      const fresh = toUser(userData);
      const caps = callerCapabilitiesFrom(userData);
      const merged: User =
        prev && prev.id === fresh.id
          ? {
              ...fresh,
              isPlatformAdmin: (caps.isPlatformAdmin ?? false) || prev.isPlatformAdmin,
              adminView: caps.adminView ?? prev.adminView,
              adminViewTrialExpiresAt:
                caps.adminViewTrialExpiresAt ?? prev.adminViewTrialExpiresAt,
              canApprove: caps.canApprove ?? prev.canApprove,
              adminEndpoints: caps.adminEndpoints ?? prev.adminEndpoints,
              launchableAgents: caps.launchableAgents ?? prev.launchableAgents,
              locationMemberships: caps.locationMemberships ?? prev.locationMemberships,
            }
          : fresh;
      useAuthStore.getState().setAuth(token, merged);
      return true;
    } catch (err) {
      if (attempt < RECONCILE_RETRY_DELAYS_MS.length) {
        // A fetch that THREW is a network-level failure (the API answered the
        // token refresh moments ago, so the backend is reachable in principle)
        // — the same transient class as the availability statuses above.
        logger.warn(
          `Identity reconciliation: /me network error, retrying in ${RECONCILE_RETRY_DELAYS_MS[attempt]}ms`,
          { attempt: attempt + 1, err },
        );
        await new Promise<void>((resolve) =>
          setTimeout(resolve, RECONCILE_RETRY_DELAYS_MS[attempt]),
        );
        continue;
      }
      logger.error("Identity reconciliation: network error", err);
      return false;
    }
  }
}
